import { ContextMenuHandler, ContextWindow } from "@asup/context-menu";
import { useState } from "react";
import { IDraggableSidebarItemResolved } from "../../controls";
import { classMerge } from "../../utility";
import { AnalysisMethodControl } from "../components/AnalysisMethodControl";
import { OperationControl } from "../components/OperationControl";
import { useArsContext } from "../contexts";
import {
  ArsSidebarItemAnalysisMethod,
  ArsSidebarItemOperation,
  type ArsSidebarItemData,
} from "./interface";

export interface OperationSidebarItemProps {
  item: IDraggableSidebarItemResolved<ArsSidebarItemData>;
  selected?: boolean;
}

/**
 * Custom sidebar item renderer for analysis methods and operations.
 * Renders analysis methods with bold styling and operations with their operation details.
 *
 * @param props - Props containing the sidebar item and its selected state
 * @param props.item - Sidebar item with discriminated union data (analysis method or operation)
 * @param props.selected - Whether the item is currently selected
 * @returns Analysis method or operation display element
 */
export const OperationSidebarItem = ({ item, selected }: OperationSidebarItemProps) => {
  const { state } = useArsContext();
  const [showWindow, setShowWindow] = useState<boolean>(false);
  const isMethod = item.data.kind === "analysisMethod";
  const isOperation = item.data.kind === "operation";
  // Type assertion needed due to discriminated union limitation with generic components
  const analysisMethod = isMethod ? (item.data as ArsSidebarItemAnalysisMethod).dropItem : null;
  const operation = isOperation ? (item.data as ArsSidebarItemOperation).dropItem : null;
  let operationControlProps: {
    dataPath: string;
    siblingCount: number;
    disableEditing?: boolean;
  } | null = null;
  if (operation) {
    for (const [analysisMethodId, method] of Object.entries(state.analysisMethods)) {
      const operationIndex = method.operations.findIndex(
        (methodOperation) => methodOperation.id === operation.id,
      );

      if (operationIndex >= 0) {
        operationControlProps = {
          dataPath: `/analysisMethods/${analysisMethodId}/operations/${operationIndex}`,
          siblingCount: method.operations.length,
          disableEditing: method.disableEditing,
        };
        break;
      }
    }
  }

  const windowTitle = analysisMethod
    ? `Analysis method: ${analysisMethod.name}`
    : `Operation: ${(item.data as ArsSidebarItemOperation).dropItem.name}`;
  const windowId = analysisMethod
    ? `analysis-method-sidebar-item-${analysisMethod.id}-context-window`
    : `operation-sidebar-item-${(item.data as ArsSidebarItemOperation).dropItem.id}-context-window`;

  return (
    <>
      <ContextMenuHandler
        className="w-100"
        menuItems={[
          {
            label: showWindow ? "Hide properties" : "Show properties",
            action: () => setShowWindow(!showWindow),
          },
        ]}
      >
        <div
          className={classMerge([
            "operation-sidebar-item",
            "px-2",
            "py-1",
            "border",
            "rounded",
            "w-100",
            selected ? "border-primary bg-primary-subtle" : "",
          ])}
          style={{ cursor: "grab" }}
        >
          {isMethod ? (
            <>
              <div className="fw-bold">{item.label}</div>
              {analysisMethod && (
                <small className="text-muted">
                  Analysis method • {analysisMethod.name}
                  {analysisMethod.disableEditing ? <em className="px-1">• Read only</em> : null}
                </small>
              )}
            </>
          ) : (
            <>
              <div className="fw-semibold">{item.label}</div>
              {operation && (
                <small className="text-muted">
                  {operation.name} {operation.label && `• ${operation.label}`}
                  {operationControlProps?.disableEditing ? (
                    <em className="px-1">• Read only</em>
                  ) : null}
                </small>
              )}
            </>
          )}
        </div>
      </ContextMenuHandler>
      <ContextWindow
        id={windowId}
        title={windowTitle}
        visible={showWindow}
        onClose={() => setShowWindow(false)}
      >
        {analysisMethod && (
          <AnalysisMethodControl
            dataPath={`/analysisMethods/${analysisMethod.id}`}
            canRename
          />
        )}
        {operation && operationControlProps && (
          <OperationControl
            dataPath={operationControlProps.dataPath}
            siblingCount={operationControlProps.siblingCount}
          />
        )}
      </ContextWindow>
    </>
  );
};

OperationSidebarItem.displayName = "OperationSidebarItem";
